<script setup lang="ts">
import Dropdown, { type DropdownChangeEvent } from "primevue/dropdown";
import { computed, ref } from "vue";
import draggable from "vuedraggable";
import AdvisorChipRow from "./AdvisorChipRow.vue";
import { advisorDropdownPt } from "./style.ts";
import type { CoachOption } from "./types.ts";
import { getConflictingOrdinals } from "./utils.ts";

const props = defineProps<{
  advisors: CoachOption[];
  options: CoachOption[];
}>();

const emit = defineEmits<{
  (e: "add", advisor: CoachOption): void;
  (e: "remove", index: number): void;
  (e: "update:ordinal", index: number, value: number | undefined): void;
  (e: "reorder", advisors: CoachOption[]): void;
  (e: "filter", query: string): void;
}>();

const showSelector = ref(false);

// Don't offer advisors already assigned within this group.
const availableOptions = computed<CoachOption[]>(() =>
  props.options.filter((option) => !props.advisors.some((advisor) => advisor.okta_id === option.okta_id)),
);

const conflictingOrdinals = computed<Set<number>>(() => getConflictingOrdinals(props.advisors));

const isConflicting = (advisor: CoachOption): boolean =>
  typeof advisor.ordinal === "number" && conflictingOrdinals.value.has(advisor.ordinal);

const onSelect = (event: DropdownChangeEvent): void => {
  if (event.value) {
    emit("add", event.value);
  }
  showSelector.value = false;
};

const onFilter = (event: { value: string }): void => {
  emit("filter", event.value ?? "");
};
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <draggable
      :model-value="advisors"
      item-key="okta_id"
      handle=".drag-handle"
      :animation="150"
      class="flex flex-col gap-1.5"
      @update:model-value="(reordered) => emit('reorder', reordered)"
    >
      <template #item="{ element, index }">
        <AdvisorChipRow
          :advisor="element"
          :conflict="isConflicting(element)"
          @remove="emit('remove', index)"
          @update:ordinal="(value) => emit('update:ordinal', index, value)"
        />
      </template>
    </draggable>

    <div v-if="conflictingOrdinals.size > 0" class="flex items-center gap-1 text-xs text-red-700">
      <span class="material-symbols-outlined text-sm">error</span>
      Contact order values must be unique within this group
    </div>

    <Dropdown
      v-if="advisors.length === 0 || showSelector"
      :options="availableOptions"
      optionLabel="displayName"
      dataKey="okta_id"
      filter
      filter-placeholder="Search"
      placeholder="Search and select advisor"
      input-class="!text-sm placeholder:text-gray-500"
      :pt="advisorDropdownPt"
      @change="onSelect"
      @filter="onFilter"
    />

    <span v-if="advisors.length === 0" class="text-xs italic text-gray-500">
      Contact order field appears after selecting an advisor
    </span>

    <button
      v-if="advisors.length > 0 && !showSelector"
      type="button"
      class="flex w-full items-center gap-1.5 rounded-md border border-dashed border-gray-400 px-2.5 py-1 text-xs text-gray-600 hover:border-teal-900 hover:bg-teal-light hover:text-teal-900"
      @click="showSelector = true"
    >
      <span class="material-symbols-outlined text-sm">add</span>
      Add another advisor
    </button>
  </div>
</template>
